import type { NodeViewRenderer } from '@tiptap/core';
import { parseGraph, graphHeightStyle } from '../content-creator/graph-model';
import { createGraphSvg } from '../content-creator/graph-svg';

export interface GraphMetadata {
    nodesJson: string;
    edgesJson: string;
    examplesJson: string;
    height?: number | string | null;
}

/** The iframe uses a standalone Editor, so node views must not require React EditorContent. */
export const createGraphNodeView: NodeViewRenderer = ({ node, editor, getPos }) => {
    const doc = (editor.options.element as HTMLElement | null)?.ownerDocument ?? document;
    const dom = doc.createElement('div');
    dom.className = 'graph-node-wrapper';
    dom.contentEditable = 'false';
    dom.style.cssText = 'margin:.75rem 0;border-radius:8px;';
    const button = doc.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', 'Edit graph');
    button.title = 'Click to edit nodes, connections and examples';
    button.style.cssText = 'display:block;width:100%;padding:.75rem;border:1px solid var(--surface-border,#aaa);border-radius:8px;background:var(--surface-ground,#f8f9fa);color:var(--text-color,#333);font:inherit;text-align:left;cursor:pointer;';
    dom.appendChild(button);
    let currentNode = node;
    const render = () => {
        button.replaceChildren();
        button.setAttribute('aria-disabled', String(!editor.isEditable));
        const heading = doc.createElement('span');
        heading.style.cssText = 'display:block;font-size:.875rem;font-weight:600;margin-bottom:.5rem;';
        button.appendChild(heading);
        try {
            const graph = parseGraph(currentNode.attrs as GraphMetadata);
            heading.textContent = `Graph (${graph.nodes.length} ${graph.nodes.length === 1 ? 'node' : 'nodes'}, ${graph.edges.length} ${graph.edges.length === 1 ? 'connection' : 'connections'}) - click to edit`;
            if (!graph.nodes.length) {
                const empty = doc.createElement('span');
                empty.textContent = 'No nodes yet. Click to build your graph.';
                button.appendChild(empty);
                return;
            }
            const svg = createGraphSvg(doc, graph);
            const height = graphHeightStyle(currentNode.attrs.height);
            if (height !== undefined) svg.style.height = height;
            svg.style.maxHeight = height === undefined ? '26rem' : 'none';
            button.appendChild(svg);
        } catch (error) {
            heading.textContent = 'Graph needs attention - click to repair';
            const message = doc.createElement('span');
            message.textContent = (error as Error).message;
            message.style.color = 'var(--red-500,#c62828)';
            button.appendChild(message);
        }
    };
    const edit = (event: MouseEvent) => {
        event.preventDefault(); event.stopPropagation();
        if (!editor.isEditable) return;
        const position = getPos();
        if (typeof position !== 'number') return;
        // Select this exact graph before the toolbar's updateAttributes command runs.
        editor.commands.setNodeSelection(position);
        editor.commands.openGraphEditor(currentNode.attrs as GraphMetadata);
    };
    button.addEventListener('click', edit);
    render();
    return {
        dom,
        update(nextNode) {
            if (nextNode.type !== currentNode.type) return false;
            currentNode = nextNode;
            render();
            return true;
        },
        selectNode() { dom.classList.add('ProseMirror-selectednode'); dom.style.boxShadow = '0 0 0 2px var(--primary-color,#3b82f6)'; },
        deselectNode() { dom.classList.remove('ProseMirror-selectednode'); dom.style.boxShadow = ''; },
        stopEvent: event => event.type !== 'keydown' || !['Backspace', 'Delete'].includes((event as KeyboardEvent).key),
        ignoreMutation: () => true,
        destroy() { button.removeEventListener('click', edit); },
    };
};
